import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  StatusBoardApi,
  type StatusBoardDto,
} from '@up2stay/up2promo-api-client';

import { usePendingClaimCount } from '@/core/hooks/use-claim-requests';
import { statusBoardQueryKeys } from '@/core/query-keys/status-board-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isAuthzApiError } from '@/core/utils/api-error.utils';

/** Default Status Board window — shares React Query cache with Home. */
export const NAV_ATTENTION_RANGE_DAYS = 30;

export type NavAttentionCounts = {
  /** Open leads with status NEW (capability-gated). */
  leads: number;
  /** Unreplied feedback entries (capability-gated). */
  feedback: number;
  /** Unseen (new) reviews in the inbox (capability-gated). */
  reviews: number;
  /** Pending product claim requests of the workspace (DEV-756). */
  claims: number;
};

export const EMPTY_NAV_ATTENTION_COUNTS: NavAttentionCounts = {
  leads: 0,
  feedback: 0,
  reviews: 0,
  claims: 0,
};

export function selectNavAttentionCounts(
  board: StatusBoardDto,
): NavAttentionCounts {
  const caps = board.capabilities;
  return {
    leads: caps.canReadLeads ? (board.leads?.newCount ?? 0) : 0,
    feedback: caps.canReadFeedback ? (board.feedback?.unrepliedCount ?? 0) : 0,
    reviews: caps.canReadReputation ? (board.reputation?.unseenCount ?? 0) : 0,
    // Not on the Status Board; merged in by `useNavAttentionCounts`.
    claims: 0,
  };
}

/**
 * Actionable nav attention from the Status Board BFF (same query as Home),
 * plus the pending claim requests (`products:read`).
 * Counts are omitted (0) when the board nulls that module for authz/entitlement.
 */
export function useNavAttentionCounts(): NavAttentionCounts {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;

  const { data } = useQuery({
    queryKey: workspaceId
      ? statusBoardQueryKeys.board(workspaceId, NAV_ATTENTION_RANGE_DAYS)
      : statusBoardQueryKeys.root,
    queryFn: async (): Promise<StatusBoardDto> => {
      if (!workspaceId) {
        throw new Error('workspaceId required');
      }
      const res = await StatusBoardApi.getBoard({
        path: { workspaceId },
        query: { rangeDays: NAV_ATTENTION_RANGE_DAYS },
      });
      return res.data;
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    select: selectNavAttentionCounts,
    retry: (failureCount, error) => !isAuthzApiError(error) && failureCount < 2,
  });

  const claims = usePendingClaimCount();
  return useMemo(
    () => ({ ...(data ?? EMPTY_NAV_ATTENTION_COUNTS), claims }),
    [data, claims],
  );
}
